var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import axios from "axios";
import { blankConfigurationMeansEmptyObject, FeatureScope, validateFeatureConfiguration, } from "healthex-common/feature-enablement.utils";
import { featureFlagConfigDecoder, featureFlagHasOptionalConfigWhenEnabled, nullDecoder } from "healthex-common/features.dtos";
import { UserType } from "healthex-common/user.dtos";
import { getFullName } from "healthex-common/utils";
import { action, computed, observable, runInAction } from "mobx";
import { Disposer } from "../../Infra/Disposer";
import { NOT_LOADED } from "../../Infra/RemoteData";
import { GLOBAL_FEATURE_ENABLEMENT_SCOPE } from "../../Logic/GlobalAdminFeatureEnablementManager";
const FEATURE_FILTER_DEBOUNCE_MS = 300;
const FEATURE_FILTER_MIN_LENGTH = 2;
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function featureHasConfigurableSettings(feature) {
    return featureFlagConfigDecoder(feature) !== nullDecoder || featureFlagHasOptionalConfigWhenEnabled(feature);
}
export function formatFeatureConfigurationJson(configuration) {
    if (configuration === null) {
        return "";
    }
    try {
        return JSON.stringify(JSON.parse(configuration), null, 2);
    }
    catch {
        return configuration;
    }
}
function createDraftFromRecord(feature, record) {
    var _a, _b;
    const configuration = (_a = record === null || record === void 0 ? void 0 : record.configuration) !== null && _a !== void 0 ? _a : null;
    return {
        enabled: (_b = record === null || record === void 0 ? void 0 : record.enabled) !== null && _b !== void 0 ? _b : false,
        configurationJson: formatFeatureConfigurationJson(configuration),
        specifyConfig: featureFlagHasOptionalConfigWhenEnabled(feature) ? configuration !== null : false,
    };
}
function draftsEqual(left, right) {
    return (left.enabled === right.enabled && left.configurationJson === right.configurationJson && left.specifyConfig === right.specifyConfig);
}
function resolveDraftConfiguration(feature, draft) {
    if (!featureHasConfigurableSettings(feature)) {
        return null;
    }
    if (featureFlagHasOptionalConfigWhenEnabled(feature) && !draft.specifyConfig) {
        return null;
    }
    const trimmed = draft.configurationJson.trim();
    if (!trimmed) {
        return blankConfigurationMeansEmptyObject(feature) ? `{}` : null;
    }
    return trimmed;
}
function formatSaveConfirmScopeTarget(scope, overview, resolvedScopeIds) {
    if (scope === FeatureScope.GLOBAL) {
        return `globally`;
    }
    if (scope === FeatureScope.ORGANIZATION) {
        const organizationId = resolvedScopeIds.organizationId;
        if (!organizationId) {
            return `this organization`;
        }
        const organizationName = overview !== NOT_LOADED ? overview.organizationName : undefined;
        return organizationName ? `${organizationName} (${organizationId})` : organizationId;
    }
    const patientId = resolvedScopeIds.patientId;
    if (!patientId) {
        return `this patient`;
    }
    const patientName = overview !== NOT_LOADED ? overview.patientName : undefined;
    return patientName ? `${patientName} (${patientId})` : patientId;
}
function toScopeOrganizationOption(organization) {
    return { id: organization.id, name: organization.name };
}
function toScopePatientOption(patient) {
    const name = getFullName(patient);
    const resolvedName = name || patient.email || undefined;
    return {
        id: patient.id,
        displayName: name && patient.email ? `${name} (${patient.email})` : resolvedName !== null && resolvedName !== void 0 ? resolvedName : patient.id,
        ...(resolvedName ? { resolvedName } : {}),
    };
}
export class GlobalAdminFeatureEnablementViewModel {
    constructor(container) {
        this.disposer = new Disposer();
        this.organizationIdInput = "";
        this.patientIdInput = "";
        this.selectedFeature = null;
        this.editingScope = null;
        this.draft = null;
        this.editBaselineDraft = null;
        this.editBaselineHasRecord = false;
        this.saveConfirmOpen = false;
        this.validationError = null;
        this.lookupLoading = false;
        this.isSaving = false;
        this.errorMessage = null;
        this.organizationIdError = null;
        this.patientIdError = null;
        this.selectedOrganizationFromSearch = null;
        this.selectedPatientFromSearch = null;
        this.sortMode = "alpha";
        this.rolloutFilterOnly = false;
        this.featureFilterInput = "";
        this.featureFilterQuery = "";
        this.featureFilterDebounceTimer = null;
        this.loadOrganizationOptions = async (inputValue) => {
            const query = inputValue.trim().toLowerCase();
            if (!query) {
                return this.organizationOptions.slice(0, 20);
            }
            return this.organizationOptions
                .filter((organization) => organization.name.toLowerCase().includes(query) || organization.id.toLowerCase().includes(query))
                .slice(0, 20);
        };
        this.loadPatientOptions = async (inputValue) => {
            const trimmed = inputValue.trim();
            if (!trimmed || UUID_PATTERN.test(trimmed)) {
                return [];
            }
            if (trimmed.includes(`@`)) {
                const results = await this.globalAdminRetrievalService.searchPatients({ email: trimmed });
                return results.map(toScopePatientOption);
            }
            const parts = trimmed.split(/\s+/).filter(Boolean);
            if (parts.length >= 2) {
                const results = await this.globalAdminRetrievalService.searchPatients({
                    firstName: parts[0],
                    lastName: parts.slice(1).join(` `),
                });
                return results.map(toScopePatientOption);
            }
            const results = await this.searchPatientsBySingleToken(trimmed);
            return results.map(toScopePatientOption);
        };
        this.globalAdminFeatureEnablementManager = container.globalAdminFeatureEnablementManager;
        this.globalAdminRetrievalService = container.globalAdminRetrievalService;
        this.organizationsManager = container.organizationsManager;
    }
    run() {
        void this.organizationsManager.organizations.waitForValue();
        void this.globalAdminFeatureEnablementManager.loadScope(GLOBAL_FEATURE_ENABLEMENT_SCOPE).catch(() => {
            runInAction(() => {
                this.errorMessage = `Failed to load feature enablement overview`;
            });
        });
        this.disposer.reaction(() => this.displayFeatures.map((item) => item.feature).join(","), () => this.ensureSelectedFeature(), { fireImmediately: true, name: "syncFeatureEnablementSelection" });
        this.disposer.reaction(() => this.selectedFeature, () => this.cancelEditing(), { name: "cancelEditOnFeatureChange" });
    }
    stop() {
        this.clearFeatureFilterDebounceTimer();
        this.disposer.dispose();
    }
    get resolvedScopeIds() {
        return this.globalAdminFeatureEnablementManager.scopeKey;
    }
    get overview() {
        return this.globalAdminFeatureEnablementManager.overview;
    }
    get scopeLookupPending() {
        const organizationId = this.organizationIdInput.trim() || undefined;
        const patientId = this.patientIdInput.trim() || undefined;
        return organizationId !== this.resolvedScopeIds.organizationId || patientId !== this.resolvedScopeIds.patientId;
    }
    get isOverviewLoading() {
        return this.overview === NOT_LOADED && !this.errorMessage && !this.organizationIdError && !this.patientIdError;
    }
    get isLoading() {
        return this.lookupLoading || this.isOverviewLoading || this.isSaving;
    }
    get organizationOptions() {
        const organizations = this.organizationsManager.organizations.value;
        if (organizations === NOT_LOADED || organizations === null) {
            return [];
        }
        return organizations
            .filter((organization) => organization.type === UserType.HEALTHCARE_ORGANIZATION || organization.type === UserType.EXTERNAL_RESEARCHER)
            .map(toScopeOrganizationOption)
            .sort((left, right) => left.name.localeCompare(right.name));
    }
    get selectedOrganizationOption() {
        var _a;
        const organizationId = this.organizationIdInput.trim();
        if (!organizationId) {
            return null;
        }
        if (((_a = this.selectedOrganizationFromSearch) === null || _a === void 0 ? void 0 : _a.id) === organizationId) {
            return this.selectedOrganizationFromSearch;
        }
        const matched = this.organizationOptions.find((organization) => organization.id === organizationId);
        if (matched) {
            return matched;
        }
        if (this.overview !== NOT_LOADED && this.overview.organizationId === organizationId && this.overview.organizationName) {
            return { id: organizationId, name: this.overview.organizationName };
        }
        return { id: organizationId, name: organizationId };
    }
    get selectedPatientOption() {
        var _a;
        const patientId = this.patientIdInput.trim();
        if (!patientId) {
            return null;
        }
        if (((_a = this.selectedPatientFromSearch) === null || _a === void 0 ? void 0 : _a.id) === patientId) {
            return this.selectedPatientFromSearch;
        }
        if (this.overview !== NOT_LOADED && this.overview.patientId === patientId && this.overview.patientName) {
            return { id: patientId, displayName: patientId, resolvedName: this.overview.patientName };
        }
        return { id: patientId, displayName: patientId };
    }
    get organizationResolvedLabel() {
        var _a;
        const organizationId = this.organizationIdInput.trim();
        if (!organizationId) {
            return null;
        }
        const matched = this.organizationOptions.find((organization) => organization.id === organizationId);
        if (matched && matched.name !== organizationId) {
            return `${matched.name} (${organizationId})`;
        }
        if (((_a = this.selectedOrganizationFromSearch) === null || _a === void 0 ? void 0 : _a.id) === organizationId && this.selectedOrganizationFromSearch.name !== organizationId) {
            return `${this.selectedOrganizationFromSearch.name} (${organizationId})`;
        }
        if (this.overview !== NOT_LOADED && this.overview.organizationId === organizationId && this.overview.organizationName) {
            return `${this.overview.organizationName} (${organizationId})`;
        }
        return null;
    }
    get patientResolvedLabel() {
        var _a;
        const patientId = this.patientIdInput.trim();
        if (!patientId) {
            return null;
        }
        if (((_a = this.selectedPatientFromSearch) === null || _a === void 0 ? void 0 : _a.id) === patientId && this.selectedPatientFromSearch.resolvedName) {
            return `${this.selectedPatientFromSearch.resolvedName} (${patientId})`;
        }
        if (this.overview !== NOT_LOADED && this.overview.patientId === patientId && this.overview.patientName) {
            return `${this.overview.patientName} (${patientId})`;
        }
        return null;
    }
    get displayFeatures() {
        if (this.overview === NOT_LOADED) {
            return [];
        }
        let features = [...this.overview.features];
        if (this.rolloutFilterOnly) {
            features = features.filter((item) => item.feature.startsWith(`RO_`));
        }
        if (this.featureFilterQuery.length >= FEATURE_FILTER_MIN_LENGTH) {
            const query = this.featureFilterQuery.toLowerCase();
            features = features.filter((item) => item.feature.toLowerCase().includes(query));
        }
        if (this.sortMode === "alpha") {
            features.sort((left, right) => left.feature.localeCompare(right.feature));
        }
        else {
            features.sort((left, right) => {
                var _a, _b, _c, _d;
                const leftTime = (_b = (_a = left.lastUpdatedAt) === null || _a === void 0 ? void 0 : _a.getTime()) !== null && _b !== void 0 ? _b : 0;
                const rightTime = (_d = (_c = right.lastUpdatedAt) === null || _c === void 0 ? void 0 : _c.getTime()) !== null && _d !== void 0 ? _d : 0;
                if (rightTime !== leftTime) {
                    return rightTime - leftTime;
                }
                return left.feature.localeCompare(right.feature);
            });
        }
        return features;
    }
    get filteredFeatureCount() {
        if (this.overview === NOT_LOADED) {
            return null;
        }
        return this.displayFeatures.length;
    }
    get featureListTitle() {
        const count = this.filteredFeatureCount;
        if (count === null) {
            return `All Features`;
        }
        return this.rolloutFilterOnly ? `Rollout Features (${count})` : `All Features (${count})`;
    }
    get selectedFeatureItem() {
        var _a;
        if (!this.selectedFeature || this.overview === NOT_LOADED) {
            return null;
        }
        return (_a = this.overview.features.find((item) => item.feature === this.selectedFeature)) !== null && _a !== void 0 ? _a : null;
    }
    get visibleScopeCards() {
        const cards = [];
        if (this.resolvedScopeIds.patientId) {
            cards.push(FeatureScope.PATIENT);
        }
        if (this.resolvedScopeIds.organizationId) {
            cards.push(FeatureScope.ORGANIZATION);
        }
        cards.push(FeatureScope.GLOBAL);
        return cards;
    }
    get scopeMatrixNames() {
        var _a, _b;
        if (this.overview === NOT_LOADED) {
            return {};
        }
        const organizationName = (_a = this.overview.organizationName) !== null && _a !== void 0 ? _a : this.overview.organizationId;
        const patientName = (_b = this.overview.patientName) !== null && _b !== void 0 ? _b : this.overview.patientId;
        return {
            ...(organizationName ? { organizationName } : {}),
            ...(patientName ? { patientName } : {}),
        };
    }
    get scopeSummaryLabel() {
        var _a, _b, _c, _d;
        const overview = this.overview !== NOT_LOADED ? this.overview : undefined;
        const parts = [];
        const organizationId = (_a = overview === null || overview === void 0 ? void 0 : overview.organizationId) !== null && _a !== void 0 ? _a : this.resolvedScopeIds.organizationId;
        if (organizationId) {
            parts.push(`Organization: ${(_b = overview === null || overview === void 0 ? void 0 : overview.organizationName) !== null && _b !== void 0 ? _b : organizationId}`);
        }
        const patientId = (_c = overview === null || overview === void 0 ? void 0 : overview.patientId) !== null && _c !== void 0 ? _c : this.resolvedScopeIds.patientId;
        if (patientId) {
            parts.push(`Patient: ${(_d = overview === null || overview === void 0 ? void 0 : overview.patientName) !== null && _d !== void 0 ? _d : patientId}`);
        }
        return parts.length === 0 ? `Global scope` : parts.join(` · `);
    }
    get hasDraftChanges() {
        if (!this.draft || !this.editBaselineDraft) {
            return false;
        }
        if (!this.editBaselineHasRecord) {
            return true;
        }
        return !draftsEqual(this.draft, this.editBaselineDraft);
    }
    get saveConfirmMessage() {
        if (!this.editingScope || !this.selectedFeature || !this.draft) {
            return ``;
        }
        const saveAction = this.draft.enabled ? `Enable` : `Disable`;
        const scopeTarget = formatSaveConfirmScopeTarget(this.editingScope, this.overview, this.resolvedScopeIds);
        if (scopeTarget === `globally`) {
            return `${saveAction} ${this.selectedFeature} globally?`;
        }
        return `${saveAction} ${this.selectedFeature} for ${scopeTarget}?`;
    }
    isEditingScope(scope) {
        return this.editingScope === scope;
    }
    showSpecifyConfigForFeature(feature) {
        return featureFlagHasOptionalConfigWhenEnabled(feature);
    }
    showConfigurationEditorForDraft(feature, draft) {
        if (!featureHasConfigurableSettings(feature)) {
            return false;
        }
        if (featureFlagHasOptionalConfigWhenEnabled(feature)) {
            return draft.specifyConfig;
        }
        return true;
    }
    async searchPatientsBySingleToken(token) {
        const [byFirstName, byLastName, byEmail] = await Promise.all([
            this.globalAdminRetrievalService.searchPatients({ firstName: token }),
            this.globalAdminRetrievalService.searchPatients({ lastName: token }),
            this.globalAdminRetrievalService.searchPatients({ email: token }),
        ]);
        const seen = new Set();
        return [...byFirstName, ...byLastName, ...byEmail].filter((patient) => {
            if (seen.has(patient.id)) {
                return false;
            }
            seen.add(patient.id);
            return true;
        });
    }
    setOrganizationIdInput(value) {
        var _a;
        this.organizationIdInput = value;
        if (((_a = this.selectedOrganizationFromSearch) === null || _a === void 0 ? void 0 : _a.id) !== value.trim()) {
            this.selectedOrganizationFromSearch = null;
        }
        this.cancelEditing();
        this.organizationIdError = null;
        this.clearError();
    }
    setPatientIdInput(value) {
        var _a;
        this.patientIdInput = value;
        if (((_a = this.selectedPatientFromSearch) === null || _a === void 0 ? void 0 : _a.id) !== value.trim()) {
            this.selectedPatientFromSearch = null;
        }
        this.cancelEditing();
        this.patientIdError = null;
        this.clearError();
    }
    selectOrganizationOption(organization) {
        var _a;
        this.organizationIdInput = (_a = organization === null || organization === void 0 ? void 0 : organization.id) !== null && _a !== void 0 ? _a : ``;
        this.selectedOrganizationFromSearch = organization;
        this.cancelEditing();
        this.organizationIdError = null;
        this.clearError();
    }
    selectPatientOption(patient) {
        var _a;
        this.patientIdInput = (_a = patient === null || patient === void 0 ? void 0 : patient.id) !== null && _a !== void 0 ? _a : ``;
        this.selectedPatientFromSearch = patient;
        this.cancelEditing();
        this.patientIdError = null;
        this.clearError();
    }
    selectFeature(feature) {
        this.selectedFeature = feature;
    }
    setSortMode(mode) {
        this.sortMode = mode;
    }
    setRolloutFilterOnly(value) {
        this.rolloutFilterOnly = value;
    }
    setFeatureFilterInput(value) {
        this.featureFilterInput = value;
        const trimmed = value.trim();
        if (!trimmed) {
            this.clearFeatureFilterDebounceTimer();
            this.featureFilterQuery = "";
            return;
        }
        this.scheduleFeatureFilterQueryUpdate(trimmed);
    }
    scheduleFeatureFilterQueryUpdate(value) {
        this.clearFeatureFilterDebounceTimer();
        this.featureFilterDebounceTimer = setTimeout(() => {
            runInAction(() => {
                this.featureFilterDebounceTimer = null;
                this.featureFilterQuery = value;
            });
        }, FEATURE_FILTER_DEBOUNCE_MS);
    }
    clearFeatureFilterDebounceTimer() {
        if (this.featureFilterDebounceTimer) {
            clearTimeout(this.featureFilterDebounceTimer);
            this.featureFilterDebounceTimer = null;
        }
    }
    clearError() {
        this.errorMessage = null;
    }
    clearScopeFieldErrors() {
        this.organizationIdError = null;
        this.patientIdError = null;
    }
    startEditing(scope) {
        const item = this.selectedFeatureItem;
        if (!item) {
            return;
        }
        const record = item[scope];
        this.editingScope = scope;
        this.draft = createDraftFromRecord(item.feature, record);
        this.editBaselineDraft = { ...this.draft };
        this.editBaselineHasRecord = record !== undefined;
        this.validationError = null;
        this.saveConfirmOpen = false;
    }
    cancelEditing() {
        this.editingScope = null;
        this.draft = null;
        this.editBaselineDraft = null;
        this.editBaselineHasRecord = false;
        this.validationError = null;
        this.saveConfirmOpen = false;
    }
    setDraftEnabled(enabled) {
        if (!this.draft) {
            return;
        }
        this.draft = { ...this.draft, enabled };
        this.validationError = null;
    }
    setDraftConfigurationJson(configurationJson) {
        if (!this.draft) {
            return;
        }
        this.draft = { ...this.draft, configurationJson };
        this.validationError = null;
    }
    setDraftSpecifyConfig(specifyConfig) {
        if (!this.draft) {
            return;
        }
        this.draft = {
            ...this.draft,
            specifyConfig,
            configurationJson: specifyConfig ? this.draft.configurationJson : "",
        };
        this.validationError = null;
    }
    validatedDraftSave() {
        const item = this.selectedFeatureItem;
        if (!item || !this.editingScope || !this.draft) {
            return null;
        }
        const configuration = resolveDraftConfiguration(item.feature, this.draft);
        const validation = validateFeatureConfiguration(item.feature, this.draft.enabled, configuration);
        if (!validation.valid) {
            this.validationError = validation.error;
            return null;
        }
        this.validationError = null;
        return { scope: this.editingScope, feature: item.feature, enabled: this.draft.enabled, configuration };
    }
    requestSave() {
        if (!this.hasDraftChanges || !this.validatedDraftSave()) {
            return;
        }
        this.saveConfirmOpen = true;
    }
    cancelSaveConfirm() {
        this.saveConfirmOpen = false;
    }
    ensureSelectedFeature() {
        if (this.overview === NOT_LOADED) {
            return;
        }
        const features = this.displayFeatures;
        if (features.length === 0) {
            this.selectedFeature = null;
            return;
        }
        if (!this.selectedFeature || !features.some((item) => item.feature === this.selectedFeature)) {
            this.selectedFeature = features[0].feature;
        }
    }
    async confirmSave() {
        const pendingSave = this.validatedDraftSave();
        if (!pendingSave) {
            this.saveConfirmOpen = false;
            return;
        }
        this.isSaving = true;
        this.saveConfirmOpen = false;
        this.clearError();
        try {
            try {
                await this.globalAdminFeatureEnablementManager.updateFeatureAtScope(pendingSave.scope, pendingSave.feature, pendingSave.enabled, pendingSave.configuration);
            }
            catch {
                this.errorMessage = `Failed to save feature enablement`;
                return;
            }
            this.cancelEditing();
            try {
                await this.globalAdminFeatureEnablementManager.loadScope(this.resolvedScopeIds);
            }
            catch {
                this.errorMessage = `Feature enablement saved, but the overview could not be refreshed`;
            }
        }
        finally {
            this.isSaving = false;
        }
    }
    async lookupScope() {
        if (this.lookupLoading) {
            return;
        }
        const organizationId = this.organizationIdInput.trim() || undefined;
        const patientId = this.patientIdInput.trim() || undefined;
        this.cancelEditing();
        this.clearError();
        this.clearScopeFieldErrors();
        this.lookupLoading = true;
        try {
            await this.globalAdminFeatureEnablementManager.loadScope({ organizationId, patientId });
        }
        catch (error) {
            if (!this.applyScopeLookupNotFoundError(error, organizationId, patientId)) {
                this.errorMessage = `Failed to load feature enablement overview`;
            }
        }
        finally {
            this.lookupLoading = false;
        }
    }
    applyScopeLookupNotFoundError(error, organizationId, patientId) {
        var _a;
        if (!axios.isAxiosError(error) || ((_a = error.response) === null || _a === void 0 ? void 0 : _a.status) !== 404) {
            return false;
        }
        const message = this.extractApiErrorMessage(error);
        if (patientId && message === `Patient not found`) {
            this.patientIdError = `Patient not found`;
            return true;
        }
        if (organizationId && message === `Organization not found`) {
            this.organizationIdError = `Organization not found`;
            return true;
        }
        return false;
    }
    extractApiErrorMessage(error) {
        var _a;
        const data = (_a = error.response) === null || _a === void 0 ? void 0 : _a.data;
        if (!this.isRecord(data) || !(`message` in data)) {
            return ``;
        }
        const message = data.message;
        if (typeof message === `string`) {
            return message;
        }
        if (Array.isArray(message)) {
            return message.join(`, `);
        }
        return ``;
    }
    isRecord(value) {
        return typeof value === `object` && value !== null;
    }
}
__decorate([
    observable
], GlobalAdminFeatureEnablementViewModel.prototype, "organizationIdInput", void 0);
__decorate([
    observable
], GlobalAdminFeatureEnablementViewModel.prototype, "patientIdInput", void 0);
__decorate([
    observable
], GlobalAdminFeatureEnablementViewModel.prototype, "selectedFeature", void 0);
__decorate([
    observable
], GlobalAdminFeatureEnablementViewModel.prototype, "editingScope", void 0);
__decorate([
    observable
], GlobalAdminFeatureEnablementViewModel.prototype, "draft", void 0);
__decorate([
    observable
], GlobalAdminFeatureEnablementViewModel.prototype, "editBaselineDraft", void 0);
__decorate([
    observable
], GlobalAdminFeatureEnablementViewModel.prototype, "editBaselineHasRecord", void 0);
__decorate([
    observable
], GlobalAdminFeatureEnablementViewModel.prototype, "saveConfirmOpen", void 0);
__decorate([
    observable
], GlobalAdminFeatureEnablementViewModel.prototype, "validationError", void 0);
__decorate([
    observable
], GlobalAdminFeatureEnablementViewModel.prototype, "lookupLoading", void 0);
__decorate([
    observable
], GlobalAdminFeatureEnablementViewModel.prototype, "isSaving", void 0);
__decorate([
    observable
], GlobalAdminFeatureEnablementViewModel.prototype, "errorMessage", void 0);
__decorate([
    observable
], GlobalAdminFeatureEnablementViewModel.prototype, "organizationIdError", void 0);
__decorate([
    observable
], GlobalAdminFeatureEnablementViewModel.prototype, "patientIdError", void 0);
__decorate([
    observable
], GlobalAdminFeatureEnablementViewModel.prototype, "selectedOrganizationFromSearch", void 0);
__decorate([
    observable
], GlobalAdminFeatureEnablementViewModel.prototype, "selectedPatientFromSearch", void 0);
__decorate([
    observable
], GlobalAdminFeatureEnablementViewModel.prototype, "sortMode", void 0);
__decorate([
    observable
], GlobalAdminFeatureEnablementViewModel.prototype, "rolloutFilterOnly", void 0);
__decorate([
    observable
], GlobalAdminFeatureEnablementViewModel.prototype, "featureFilterInput", void 0);
__decorate([
    observable
], GlobalAdminFeatureEnablementViewModel.prototype, "featureFilterQuery", void 0);
__decorate([
    computed
], GlobalAdminFeatureEnablementViewModel.prototype, "resolvedScopeIds", null);
__decorate([
    computed
], GlobalAdminFeatureEnablementViewModel.prototype, "overview", null);
__decorate([
    computed
], GlobalAdminFeatureEnablementViewModel.prototype, "scopeLookupPending", null);
__decorate([
    computed
], GlobalAdminFeatureEnablementViewModel.prototype, "isOverviewLoading", null);
__decorate([
    computed
], GlobalAdminFeatureEnablementViewModel.prototype, "isLoading", null);
__decorate([
    computed
], GlobalAdminFeatureEnablementViewModel.prototype, "organizationOptions", null);
__decorate([
    computed
], GlobalAdminFeatureEnablementViewModel.prototype, "selectedOrganizationOption", null);
__decorate([
    computed
], GlobalAdminFeatureEnablementViewModel.prototype, "selectedPatientOption", null);
__decorate([
    computed
], GlobalAdminFeatureEnablementViewModel.prototype, "organizationResolvedLabel", null);
__decorate([
    computed
], GlobalAdminFeatureEnablementViewModel.prototype, "patientResolvedLabel", null);
__decorate([
    computed
], GlobalAdminFeatureEnablementViewModel.prototype, "displayFeatures", null);
__decorate([
    computed
], GlobalAdminFeatureEnablementViewModel.prototype, "filteredFeatureCount", null);
__decorate([
    computed
], GlobalAdminFeatureEnablementViewModel.prototype, "featureListTitle", null);
__decorate([
    computed
], GlobalAdminFeatureEnablementViewModel.prototype, "selectedFeatureItem", null);
__decorate([
    computed
], GlobalAdminFeatureEnablementViewModel.prototype, "visibleScopeCards", null);
__decorate([
    computed
], GlobalAdminFeatureEnablementViewModel.prototype, "scopeMatrixNames", null);
__decorate([
    computed
], GlobalAdminFeatureEnablementViewModel.prototype, "scopeSummaryLabel", null);
__decorate([
    computed
], GlobalAdminFeatureEnablementViewModel.prototype, "hasDraftChanges", null);
__decorate([
    computed
], GlobalAdminFeatureEnablementViewModel.prototype, "saveConfirmMessage", null);
__decorate([
    action
], GlobalAdminFeatureEnablementViewModel.prototype, "setOrganizationIdInput", null);
__decorate([
    action
], GlobalAdminFeatureEnablementViewModel.prototype, "setPatientIdInput", null);
__decorate([
    action
], GlobalAdminFeatureEnablementViewModel.prototype, "selectOrganizationOption", null);
__decorate([
    action
], GlobalAdminFeatureEnablementViewModel.prototype, "selectPatientOption", null);
__decorate([
    action
], GlobalAdminFeatureEnablementViewModel.prototype, "selectFeature", null);
__decorate([
    action
], GlobalAdminFeatureEnablementViewModel.prototype, "setSortMode", null);
__decorate([
    action
], GlobalAdminFeatureEnablementViewModel.prototype, "setRolloutFilterOnly", null);
__decorate([
    action
], GlobalAdminFeatureEnablementViewModel.prototype, "setFeatureFilterInput", null);
__decorate([
    action
], GlobalAdminFeatureEnablementViewModel.prototype, "clearError", null);
__decorate([
    action
], GlobalAdminFeatureEnablementViewModel.prototype, "clearScopeFieldErrors", null);
__decorate([
    action
], GlobalAdminFeatureEnablementViewModel.prototype, "startEditing", null);
__decorate([
    action
], GlobalAdminFeatureEnablementViewModel.prototype, "cancelEditing", null);
__decorate([
    action
], GlobalAdminFeatureEnablementViewModel.prototype, "setDraftEnabled", null);
__decorate([
    action
], GlobalAdminFeatureEnablementViewModel.prototype, "setDraftConfigurationJson", null);
__decorate([
    action
], GlobalAdminFeatureEnablementViewModel.prototype, "setDraftSpecifyConfig", null);
__decorate([
    action
], GlobalAdminFeatureEnablementViewModel.prototype, "validatedDraftSave", null);
__decorate([
    action
], GlobalAdminFeatureEnablementViewModel.prototype, "requestSave", null);
__decorate([
    action
], GlobalAdminFeatureEnablementViewModel.prototype, "cancelSaveConfirm", null);
__decorate([
    action
], GlobalAdminFeatureEnablementViewModel.prototype, "ensureSelectedFeature", null);
